/* @jsxImportSource @emotion/react */

import React from 'react'
import classNames from 'classnames'
import { useIntl } from 'react-intl'

import { brandColorBackground, isLightColor } from '@slite/components'

import type { CustomerStoryCardData } from './customerStoryCardFields'
import { INDUSTRY_LABELS } from './industries'

const cardCss = {
  height: 400,
  transition: 'transform .2s cubic-bezier(.215,.61,.355,1)',
  '&:hover': { transform: 'translateY(-4px)' },
} as const

const logoAreaCss = { flex: 1, minHeight: 0 } as const

const logoCss = {
  // Capped at a single card's 50%, so a double-width card doesn't grow its logo.
  maxWidth: 'min(50%, 170px)',
  maxHeight: 56,
  objectFit: 'contain',
} as const

const titleCss = {
  lineHeight: 1.25,
  maxWidth: 480,
  textWrap: 'balance',
} as const

export function CustomerStoryCard(props: {
  caseStudy: CustomerStoryCardData
  href: string
  className?: string
}) {
  const intl = useIntl()
  const { caseStudy } = props

  const logoUrl = caseStudy.companyLogo?.url
  const brandColor = caseStudy.logoBackgroundColor
  // A logo is only drawn for its brand colour (often white on it), so the
  // card shows one only when it has both.
  const brand = logoUrl && brandColor ? { logoUrl, color: brandColor } : null
  const hasLightText = brand !== null && !isLightColor(brand.color)

  const firstIndustry = caseStudy.industries?.[0]
  const industryLabel =
    firstIndustry && INDUSTRY_LABELS[firstIndustry]
      ? intl.formatMessage(INDUSTRY_LABELS[firstIndustry])
      : null
  const publishedAt = caseStudy.date ?? caseStudy._createdAt

  return (
    <a
      href={props.href}
      className={classNames(
        'flexbox flexbox-column padding-24 radius-semilarger overflow-hidden',
        hasLightText ? 'full-white' : 'shade-00',
        !brand && 'background-shade-100',
        props.className
      )}
      css={[cardCss, brand && brandColorBackground(brand.color)]}
    >
      <div
        className="font-weight-semibold"
        css={{ fontSize: 13, minHeight: 16 }}
      >
        {industryLabel}
      </div>

      <div
        className="flexbox flexbox-centered padding-vertical-24"
        css={logoAreaCss}
      >
        {brand && (
          <img
            src={brand.logoUrl}
            alt={caseStudy.companyLogo?.alt ?? ''}
            loading="lazy"
            css={logoCss}
          />
        )}
      </div>

      <h3
        className="font-family-title font-weight-medium font-size-4"
        css={titleCss}
      >
        {caseStudy.title}
      </h3>
      <time dateTime={publishedAt} className="font-size-8 margin-top-12">
        {intl.formatDate(publishedAt, { dateStyle: 'medium' })}
      </time>
    </a>
  )
}
